import {
  DATE_PRESETS,
  FLIPKART_BRAND_ID,
  GA_ACTION,
  GA_CATEGORIES,
  IMAGE_FILE_EXTENSIONS,
  IMAGE_PREFIX,
  IMAGE_PREFIX_AZURE,
  IMAGE_PREFIX_SOURCING_ASSETS,
  LINK_REGEX,
  MEESHO_PILOT_3_BRAND_ID,
  NON_PRODUCT_LINK_IDENTIFIERS,
  POST_TYPES,
  PURE_ORIGIN_BRAND_ID,
  ZERO_COMMISSION,
} from "./constants";
import { getAnalytics, setUserId } from "firebase/analytics";
import {
  getAppBuildVersion,
  getAppSessionUuid,
  getPlatform,
} from "../utils/config";
import { StyledImage } from "@wish-ui";

import { auth } from "./firebase";
import { saveState } from "./localStorage";
import { triggerGa } from "./gaUtils";

export const addHours = (date, hours) => {
  const dateCopy = new Date(date);
  dateCopy.setTime(dateCopy.getTime() + hours * 60 * 60 * 1000);
  return dateCopy;
};

export const refreshFirebaseAuthStatusOrSignOut = async () => {
  const currentUser = auth.currentUser;
  if (currentUser) {
    const token = await currentUser.getIdToken();
    saveState("tokenExpiry", addHours(new Date(), 1));
    saveState("firebaseToken", token);
  } else {
    await signOutFirebaseAuth();
  }
};

export const signOutFirebaseAuth = async () => {
  const creatorSourceFromLocalStorage = localStorage.getItem("creatorSource");
  localStorage.clear();
  if (creatorSourceFromLocalStorage) {
    localStorage.setItem("creatorSource", creatorSourceFromLocalStorage);
  }
  await auth.signOut();
};

export const getResponsiveWidth = (widthFactor = 100) => {
  return window.innerWidth > 448
    ? `${(28 * widthFactor) / 100}rem`
    : window.innerWidth < 360
    ? `${(22.5 * widthFactor) / 100}rem`
    : `${(window.innerWidth * widthFactor) / 100 / 16}rem`;
};

export const copyToClipboard = async (textToCopy) => {
  await window.customCopy(textToCopy, navigator);
};

const openAndroidShare = ({ textToShare }) => {
  window?.ReactNativeWebView?.postMessage(
    JSON.stringify({ type: "share", payload: textToShare })
  );
};

export const openShareDrawer = async (textToShare) => {
  openAndroidShare({ textToShare });
  try {
    await window.customShare(textToShare, navigator);
  } catch (e) {
    // console.log("error opening share drawer", e);
  }
};

export const copyToClipboardAndOpenShareDrawer = async (textToCopyAndShare) => {
  openAndroidShare({ textToShare: textToCopyAndShare });
  try {
    await window.customCopy(textToCopyAndShare, navigator);
    await window.customShare(textToCopyAndShare, navigator);
  } catch (e) {
    // console.log("error opening share drawer", e);
  }
};

export const openLinkInNewTab = (linkToOpen) => {
  openExternalLinkInNewTab({ redirectUrl: linkToOpen });
};

export const formatShortDate = (date) => {
  if (!date) return "";
  const dateObj = new Date(date);
  if (isNaN(dateObj)) return "";
  const day = dateObj.getDate();
  const month = dateObj.toLocaleString("en-US", { month: "short" });
  const year = dateObj.getFullYear();

  return `${day} ${month}, ${year}`;
};
export const getFormattedDate = (date) => {
  return new Date(date).toLocaleDateString("en-us", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const getFormattedDateWithoutYear = (date) => {
  return new Date(date).toLocaleDateString("en-us", {
    month: "short",
    day: "numeric",
  });
};

const allCommissionAreSameNonZero = (products) => {
  if (!products?.length) return false;
  let commission = products[0]?.commission;
  for (let i = 1; i < products.length; i++) {
    if (products[i]?.commission !== commission) {
      return false;
    }
  }

  return true;
};

const allCommissionsAreZero = (products) => {
  for (let i = 0; i < products.length; i++) {
    if (products[i]?.commission !== ZERO_COMMISSION) {
      return false;
    }
  }
  return true;
};

export const getCommissionRangeForAnalytics = (products) => {
  if (!products || products?.length === 0) {
    return ZERO_COMMISSION;
  }
  if (allCommissionsAreZero(products)) {
    return "Non commissionable";
  } else if (allCommissionAreSameNonZero(products)) {
    if (
      products.some(
        (product) =>
          product?.commission_new !== product?.commission_old ||
          product?.category_wise_commissions?.length ||
          product?.commission_additional_info_text?.length
      )
    ) {
      return `Upto ${products[0]?.commission}`;
    } else {
      return `Flat ${products[0]?.commission}`;
    }
  } else {
    let minCommission = 100;
    let maxCommission = 0;
    for (let i = 0; i < products.length; i++) {
      let productCommission = products[i]?.commission
        ? parseFloat(products[i]?.commission)
        : 0;
      if (productCommission > maxCommission) {
        maxCommission = productCommission;
      }
      if (productCommission < minCommission) {
        minCommission = productCommission;
      }
    }
    if (minCommission === maxCommission) {
      return `${minCommission}%`;
    } else {
      return `Upto ${maxCommission}%`;
    }
  }
};

export const getBrandCategoryWiseMaxCommission = (
  commissions,
  live_commission
) => {
  if (
    commissions === undefined ||
    commissions?.creator_specific_commission_exists === true
  )
    return "";
  if (commissions.length === 0) {
    return live_commission;
  }
  const range = commissions.reduce(
    (acc, commission) => {
      if (commission.creator_commission_pct < acc.min) {
        acc.min = commission.creator_commission_pct;
      }
      if (commission.creator_commission_pct > acc.max) {
        acc.max = commission.creator_commission_pct;
      }
      return acc;
    },
    { min: Infinity, max: -Infinity }
  );
  return ` ${range.min}% - ${range.max}%`;
};

export const getDataPointLoadingValue = (dataPoint) => {
  return dataPoint?.status === "pending";
};

export const getAssetUrl = (assetUrl) => {
  return IMAGE_PREFIX + assetUrl;
};

export const getAssetUrlAzure = (assetUrl) => {
  return IMAGE_PREFIX_AZURE + assetUrl;
};

export const getSourcingAssetUrl = (assetUrl) => {
  return IMAGE_PREFIX_SOURCING_ASSETS + assetUrl;
};

export const getImageUrlFromCategoryNamePng = (categoryName) => {
  if (categoryName === "") return "";
  return IMAGE_PREFIX_SOURCING_ASSETS + categoryName + ".png";
};

export const getImageUrlFromCategoryNameJpeg = (categoryName) => {
  return IMAGE_PREFIX_SOURCING_ASSETS + categoryName + ".jpeg";
};

export const getOpacityForDisabledValue = (disabled) => {
  return disabled ? "0.4" : "1";
};

export function getCroppedImg(image, crop) {
  const canvas = document.createElement("canvas");
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  canvas.width = crop.width * 4;
  canvas.height = crop.height * 4;
  const ctx = canvas.getContext("2d");

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width * 4,
    crop.height * 4
  );
  // As a blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        blob.name = "croppedImage";
        resolve(blob);
      },
      "image/jpeg",
      1
    );
  });
}
export const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const getDisplayDateRange = (startDate, endDate) => {
  return `${startDate?.getDate()} ${
    MONTHS[startDate?.getMonth()]
  } - ${endDate?.getDate()} ${MONTHS[endDate?.getMonth()]}`;
};

export const getStartAndEndDateFromPreset = (preset) => {
  let today = new Date();
  let startDate = new Date();
  let endDate = new Date();
  if (preset === DATE_PRESETS.LAST_MONTH) {
    startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    endDate = new Date(today.getFullYear(), today.getMonth(), 0);
  } else if (preset === DATE_PRESETS.YESTERDAY) {
    endDate = new Date(today.setDate(today.getDate() - 1));
    startDate = endDate;
  } else if (preset === DATE_PRESETS.TODAY) {
    startDate = today;
  } else if (preset === DATE_PRESETS.ALL_TIME) {
    startDate = new Date(2020, 1, 1, 0, 0, 0, 0);
  } else if (preset === DATE_PRESETS.THIS_MONTH) {
    startDate = new Date(today.getFullYear(), today.getMonth(), 1);
  } else if (preset === DATE_PRESETS.LAST_2_MONTHS) {
    startDate = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    endDate = new Date(today.getFullYear(), today.getMonth(), 0);
  } else if (preset === DATE_PRESETS.LAST_3_MONTHS) {
    startDate = new Date(today.getFullYear(), today.getMonth() - 3, 1);
    endDate = new Date(today.getFullYear(), today.getMonth(), 0);
  } else if (preset === DATE_PRESETS.LAST_6_MONTHS) {
    startDate = new Date(today.getFullYear(), today.getMonth() - 6, 1);
    endDate = new Date(today.getFullYear(), today.getMonth(), 0);
  } else if (preset === DATE_PRESETS.LAST_7_DAYS) {
    startDate = new Date(today.setDate(today.getDate() - 6));
  } else if (preset === DATE_PRESETS.LAST_TO_LAST_MONTH) {
    startDate = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    endDate = new Date(today.getFullYear(), today.getMonth() - 1, 0);
  }
  return [startDate, endDate];
};

export const formatDate = (date) => {
  let year = date.toLocaleString("default", { year: "numeric" });
  let month = date.toLocaleString("default", { month: "2-digit" });
  let day = date.toLocaleString("default", { day: "2-digit" });
  return year + "-" + month + "-" + day;
};

export function getDateObjectFromDateString(date) {
  let dateArray = date?.split("-");
  let dateObject = null;
  if (dateArray?.length === 3)
    dateObject = new Date(
      parseInt(dateArray[0]),
      parseInt(dateArray[1]) - 1,
      parseInt(dateArray[2])
    );
  return dateObject;
}

export const getDisplayTextFromDatesAndPresets = (
  startDate,
  endDate,
  preset,
  allowedPresets
) => {
  if (preset && allowedPresets?.includes(preset)) {
    return preset;
  } else {
    let startDateToDate = new Date(startDate);
    let endDateToDate = new Date(endDate);
    return getDisplayDateRange(startDateToDate, endDateToDate);
  }
};

export const getShareAllLinksText = (
  creatorUsername,
  postId,
  type,
  products,
  postLink = ""
) => {
  let text = "";
  if (type === "collection") {
    text = `*Collection Link:* ${getPostURL(
      creatorUsername,
      postId,
      type
    )}\n\n*Short link for stories:* \n\n${getAllProductShortUrls(products)}`;
  } else {
    if (postLink?.includes("instagram.com")) {
      text = `*Post Link:* ${getPostURL(
        creatorUsername,
        postId,
        type
      )}\n\n*Short link for stories:* \n\n${getAllProductShortUrls(products)}`;
    } else {
      text = `*All Product Links:* ${getPostURL(
        creatorUsername,
        postId,
        type
      )}\n\n*Individual Product Links:* \n\n${getAllProductShortUrls(
        products
      )}`;
    }
  }
  return text;
};

export const getAllProductShortUrls = (products) => {
  return products
    .filter((prod) => prod.is_alive)
    .map(
      (product, i) =>
        `${i + 1}. ${product?.name}: ${
          product?.shorturl || product?.short_url
        }\n`
    )
    .join("\n");
};

export const getPostURL = (creatorUsername, postId, kind) => {
  return `https://wishlink.com/${creatorUsername}/${kind}/${postId}`;
};

export const formatAmountK = (amount) => {
  let formattedAmount = "";
  let isNegative = false;
  let magnitude = 0;
  if (typeof amount != "number") {
    return formattedAmount;
  }
  if (amount < 0) {
    isNegative = true;
  }
  amount = Math.abs(Math.ceil(amount));
  if (amount >= 1000) {
    magnitude += 1;
    amount = amount / 1000;
    while (amount >= 100 && magnitude < 3) {
      magnitude += 1;
      amount = amount / 100;
    }
  }
  amount = Number(amount.toPrecision(3));
  return (
    (isNegative ? "-" : "") +
    amount?.toString() +
    ["", "K", "L", "Cr"][magnitude]
  );
};

export const createDIYPostObj = (inputObj, type) => {
  const diyPost = {};
  if (type === "instagram" || type === "facebook") {
    diyPost.post_url = inputObj?.permalink;
    diyPost.media_type = inputObj?.media_type;
    diyPost.thumbnail_url = inputObj?.thumbnail_url || inputObj?.media_url;
    diyPost.media_url = inputObj?.media_url;
    diyPost.post_social_media_id = inputObj?.id;
    diyPost.post_added_on_social_media = inputObj?.timestamp;
  } else if (type === "youtube") {
    diyPost.post_url = inputObj?.post_url;
    diyPost.media_type = "VIDEO";
    diyPost.thumbnail_url = `https://img.youtube.com/vi/${inputObj?.contentDetails?.videoId}/0.jpg`;
    diyPost.media_url = `https://www.youtube.com/embed/${inputObj?.contentDetails?.videoId}`;
    diyPost.post_social_media_id = "";
    diyPost.post_added_on_social_media = inputObj?.snippet?.publishedAt;
    diyPost.privacy_status = inputObj?.status?.privacyStatus;
  } else {
    // console.log("We shouldn't have been here :(");
  }
  return diyPost;
};

export const triggerGoBackGa = (pageName, screenName) => {
  triggerGa({
    category: GA_CATEGORIES.NAVIGATION,
    action: GA_ACTION.BACK,
    pageName: pageName,
    screenName: screenName,
  });
  return true;
};

export const getDIYGaBasedOnFirstPost = (firstPostExists) => {
  return firstPostExists ? GA_CATEGORIES.CREATE : GA_CATEGORIES.FIRST_POST;
};

export const triggerGAUserId = (creatorId) => {
  let adminToken = localStorage.adminToken;
  if (!adminToken) {
    const analytics = getAnalytics();
    setUserId(analytics, creatorId);
    window.gtag("config", "G-9K7DV3BSPQ", {
      user_id: creatorId,
      platform: getPlatform(),
      app_build: getAppBuildVersion(),
      app_open_id: getAppSessionUuid(),
    });
  }
};

export const getInstaLoginUrl = (isEngageViaInstagram) => {
  let origin = window.location.origin;
  if (window.ReactNativeWebView) {
    origin = origin.replace("app://", "https://");
  }
  if (isEngageViaInstagram) {
    return `https://www.instagram.com/oauth/authorize?wishlink_external=true&client_id=1328969108268863&redirect_uri=${origin}/auth/code&response_type=code&scope=instagram_business_basic%2Cinstagram_business_manage_messages%2Cinstagram_business_manage_comments%2Cinstagram_business_manage_insights`;
  } else {
    return `https://www.instagram.com/oauth/authorize?wishlink_external=true&client_id=1328969108268863&redirect_uri=${origin}/auth/code&response_type=code&scope=instagram_business_basic`;
  }
};

export const triggerInstagramLogin = (isEngageViaInstagram = false) => {
  const adminToken = localStorage.getItem("adminToken");
  if (adminToken) {
    return;
  }
  const redirectUrl = getInstaLoginUrl(isEngageViaInstagram);
  openExternalLinkInNewTab({
    redirectUrl,
  });
};

export const triggerFacebookLogin = () => {
  const adminToken = localStorage.getItem("adminToken");
  if (adminToken) {
    return;
  }
  let redirect_uri =
    window.location.origin.replace("http://", "https://") +
    "/facebook/callback";

  if (window.ReactNativeWebView) {
    redirect_uri = redirect_uri.replace("app://", "https://");
  }
  const client_id = "665365291834413";
  const response_type = "code";

  const scope = [
    "email",
    "read_insights",
    "public_profile",
    "pages_show_list",
    "pages_manage_metadata",
    "pages_messaging",
    "pages_read_engagement",
    "pages_show_list",
    "business_management",
    "pages_manage_posts",
    "pages_manage_engagement",
    "publish_video",
  ];

  const redirectUrl = `https://www.facebook.com/v16.0/dialog/oauth?client_id=${client_id}&redirect_uri=${redirect_uri}&response_type=${response_type}&scope=${scope}`;
  openExternalLinkInNewTab({ redirectUrl });
};

export const triggerPinterestLogin = () => {
  openExternalLinkInNewTab({
    redirectUrl: `https://www.pinterest.com/oauth/?response_type=code&redirect_uri=${window.location.origin.replace(
      "app://",
      "https://"
    )}/pinterest-callback&client_id=1501683&scope=boards:read,boards:write,pins:read,pins:write,user_accounts:read`,
  });
};

export const loadGoogleScript = (callback) => {
  if (document.getElementById("google-youtube-signin")) {
    if (callback) callback();
    return;
  }
  const googleScript = document.createElement("script");
  googleScript.src = "https://accounts.google.com/gsi/client";
  googleScript.defer = true;
  googleScript.id = "google-youtube-signin";
  if (callback) {
    googleScript.onload = () => callback();
  }
  document.head.appendChild(googleScript);
};

export const triggerYoutubeLogin = async () => {
  const adminToken = localStorage.getItem("adminToken");
  if (adminToken) {
    return;
  }
  const autoLoginApp = localStorage.getItem("loginApp");
  if (autoLoginApp && autoLoginApp === "yt") {
    await signOutFirebaseAuth();
  }
  /* global google */
  const client = google.accounts.oauth2.initCodeClient({
    client_id:
      "429224986687-r19fp48m6j5g1ri7spb3d659ig0pvknu.apps.googleusercontent.com",
    // eslint-disable-next-line no-useless-escape
    scope: `https://www.googleapis.com/auth/youtube.readonly \ https://www.googleapis.com/auth/youtubepartner \ https://www.googleapis.com/auth/yt-analytics.readonly`,
    ux_mode: "redirect",
    redirect_uri: `${window.location.origin}/youtube-auth`,
    access_type: "offline",
    callback: () => {},
  });
  client.requestCode();
};

export const triggerEngageLogin = (
  redirect_uri = window.location.origin + "/auth/success"
) => {
  const client_id = "665365291834413";
  if (window.cordova) {
    redirect_uri = redirect_uri.replace("app://", "https://");
  }
  const response_type = "token";

  const scope = [
    "email",
    "public_profile",
    "pages_show_list",
    "instagram_basic",
    "instagram_manage_insights",
    "instagram_manage_comments",
    "instagram_manage_messages",
    "pages_manage_metadata",
    "pages_messaging",
    "pages_read_engagement",
    "pages_show_list",
    "business_management",
    "pages_manage_posts",
    "publish_video",
    "pages_manage_engagement",
  ];

  const redirectUrl = `https://www.facebook.com/v16.0/dialog/oauth?client_id=${client_id}&redirect_uri=${redirect_uri}&response_type=${response_type}&scope=${scope}`;
  openExternalLinkInNewTab({ redirectUrl });
};

export const preloadImage = (url) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = url;
    image.onload = () => resolve(url); // Resolve with URL to identify the image that loaded
    image.onerror = (error) =>
      reject(new Error(`Failed to load image at ${url}: ${error.message}`));
  });
};

export const preloadImages = (urls) => {
  const promises = urls.map((url) => preloadImage(url));
  return Promise.all(promises)
    .then((results) => {
      return results;
    })
    .catch((error) => {
      //console.error("Error loading images:", error);
    });
};

export const checkIfLinkIsValidScrapingLink = (
  link,
  scrapingFailureDomains = [],
  username = ""
) => {
  let returnObject = { success: true, message: "" };
  if (username === "wishlinkconnect" || username === "trialroom") {
    return returnObject;
  }
  if (!link) {
    returnObject.message = "Please add a link";
  }
  if (typeof link !== "string") {
    returnObject.message = "Please add a link";
  }
  link = link?.toLowerCase();
  let isUrlType = LINK_REGEX.test(link);
  if (!isUrlType) {
    returnObject.message =
      "Please enter a product link, this does not seem to be a link.";
  }
  if (
    NON_PRODUCT_LINK_IDENTIFIERS.some((str) => link.includes(str.toLowerCase()))
  ) {
    returnObject.message =
      "Please add a product link, this seems like a social media link";
  }
  if (
    link.includes("wishlink.com/") &&
    !(
      link.includes("wishlink.com/share/") || link.includes("wishlink.com/buy/")
    ) &&
    username !== "wishlinksrishti" &&
    username !== "wishlinkconnect"
  ) {
    returnObject.message =
      "Please add a product link, this seems like a wishlink storefront url";
  }
  try {
    let processedUrl = new URL(link);
    let path = processedUrl.pathname;
    for (const str of IMAGE_FILE_EXTENSIONS) {
      if (path.endsWith(str)) {
        returnObject.message =
          "Please add a product link, this seems like an image url";
        break;
      }
    }
  } catch (e) {
    returnObject.message = "Please add a valid product link";
  }
  for (const domain of scrapingFailureDomains) {
    if (link?.includes(domain)) {
      returnObject.message = "Please add a valid product link";
      break;
    }
  }
  if (returnObject.message !== "") {
    returnObject.success = false;
  }
  return returnObject;
};

export const checkInvalidLinkExists = (links, scrapingFailureDomains = []) => {
  let invalidLinkExists = false;
  for (const link of links) {
    let validity = checkIfLinkIsValidScrapingLink(link, scrapingFailureDomains);
    if (!validity.success && link !== "") {
      invalidLinkExists = true;
      break;
    }
  }
  return invalidLinkExists;
};

export const checkValidLinkExists = (links, scrapingFailureDomains = []) => {
  let validLinkExists = false;
  for (const link of links) {
    let validity = checkIfLinkIsValidScrapingLink(link, scrapingFailureDomains);
    if (validity.success) {
      validLinkExists = true;
      break;
    }
  }
  return validLinkExists;
};

export const getTimeAgoTextFromDateAndCurrentTimestamps = (
  dateSent,
  dateToday
) => {
  let dateDelta = dateToday - dateSent;
  let daysDelta = Math.floor(dateDelta / (1000 * 60 * 60 * 24));

  if (daysDelta < 0) {
    return "now";
  } else if (daysDelta === 0) {
    let secondsDelta = Math.floor(dateDelta / 1000);

    if (secondsDelta < 60) {
      if (secondsDelta === 0) {
        return "now";
      }
      return `${secondsDelta}s`;
    } else if (secondsDelta >= 60 && secondsDelta < 3600) {
      let minutesDelta = Math.floor(secondsDelta / 60);
      return `${minutesDelta}m`;
    } else {
      let hoursDelta = Math.floor(secondsDelta / 3600);
      return `${hoursDelta}h`;
    }
  } else if (daysDelta >= 1 && daysDelta < 7) {
    return `${daysDelta}d`;
  } else if (daysDelta >= 7 && daysDelta < 30) {
    let weeksDelta = Math.floor(daysDelta / 7);
    return `${weeksDelta}w`;
  } else if (daysDelta >= 30 && daysDelta < 360) {
    let monthsDelta = Math.floor(daysDelta / 30);
    return `${monthsDelta}M`;
  } else {
    if (daysDelta < 365) {
      return "1y";
    } else {
      let yearsDelta = Math.floor(daysDelta / 365);
      return `${yearsDelta}y`;
    }
  }
};

export const getkFormatting = (int) => {
  if (typeof int === "string") {
    int = parseInt(int);
  }
  if (int >= 100000) {
    int = (int / 100000).toFixed(2);
    int = int + "L";
    return int;
  }
  if (int >= 1000) {
    int = (int / 1000).toFixed(2);
    int = int + "K";
    return int;
  }
  return int.toFixed(2);
};
export const getkFormattingWithoutDecimal = (
  int,
  hideZeroAfterDecimal = false
) => {
  if (typeof int === "string") {
    int = parseInt(int);
  }

  if (int >= 10000000) {
    int = (int / 10000000).toFixed(2);
    int = int + "Cr";
    return int;
  }

  if (int >= 100000) {
    int = (int / 100000).toFixed(2);
    int = int + "L";
    return int;
  }

  if (int >= 1000) {
    int = (int / 1000).toFixed(1);
    int = int + "K";
    return int;
  }

  if (int < 10) {
    if (hideZeroAfterDecimal) return int;
    return int.toFixed(1);
  }

  return Math.floor(int);
};

export const formatViews = (views) => {
  if (!views || views === "NA") return "NA";

  const num = parseInt(views);
  if (isNaN(num)) return "NA";

  if (num < 1000) {
    return num.toString();
  } else if (num >= 1000 && num < 1000000) {
    const kValue = (num / 1000).toFixed(1);
    return kValue.endsWith(".0") ? `${kValue.slice(0, -2)}k` : `${kValue}k`;
  } else if (num >= 1000000) {
    const mValue = (num / 1000000).toFixed(1);
    return mValue.endsWith(".0") ? `${mValue.slice(0, -2)}M` : `${mValue}M`;
  }

  return num.toString();
};

const maxCommssionFromCategory = (category, liveCommission) => {
  if (!category || category?.creator_specific_commission_exists === true)
    return "";
  if (category.length === 0 && liveCommission) {
    return liveCommission;
  }
  let maxCommission = 0;
  category?.forEach((brand) => {
    if (brand.creator_commission_pct > maxCommission) {
      maxCommission = brand.creator_commission_pct;
    }
  });
  return maxCommission;
};

export const getMaxCategoryWiseBrandCommission = (category, liveCommission) => {
  if (!category || category?.creator_specific_commission_exists === true)
    return "";
  const maxCommission = maxCommssionFromCategory(category, liveCommission);
  return `upto ${maxCommission}%`;
};

export const redirectUserToExternalLink = (external_link) => {
  openExternalLinkInNewTab({ redirectUrl: external_link });
};

export const openExternalLinkInNewTab = ({ redirectUrl }) => {
  const build = getAppBuildVersion();
  if (window.ReactNativeWebView) {
    if (build <= 24) {
      redirectUrl = redirectUrl.includes("?")
        ? redirectUrl + "&wishlink_external=true"
        : redirectUrl + "?wishlink_external=true";
      const target = window.cordova ? "_self" : "_blank";
      window.open(redirectUrl, target, "noreferrer");
    } else {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "open_external_link",
          payload: { redirectUrl },
        })
      );
    }
  } else {
    window.open(redirectUrl, "_blank", "noreferrer");
  }
};

const UPTO_BRAND_IDS = [PURE_ORIGIN_BRAND_ID, FLIPKART_BRAND_ID];

export const getUptoOrFlatCommissionText = (BrandId) => {
  if (UPTO_BRAND_IDS.includes(BrandId)) {
    return "Upto";
  }
  return "Flat";
};

export async function convertBlobUrlToBase64(blobObj) {
  const blobUrlString = URL.createObjectURL(blobObj);
  const response = await fetch(blobUrlString, {
    headers: {
      Accept: "image/*", // Ensure the server knows you expect an image
    },
  });
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64data = reader.result;
      resolve(base64data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export const shareBlobImage = async ({ message, blob }) => {
  const platform = getPlatform();
  const build = getAppBuildVersion();
  const imageData = await convertBlobUrlToBase64(blob);
  if (platform === "android" && build >= 23) {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "share_base64_image",
        payload: {
          options: { message: message },
          imageUrl: imageData,
        },
      })
    );
    return true;
  }
  return false;
};

export const hasCommissionDetails = (listItem) => {
  return (
    listItem?.commission !== "0%" ||
    listItem?.commission_new !== "0%" ||
    listItem?.commission_old !== "0%" ||
    listItem?.category_wise_commissions?.length > 0 ||
    listItem?.commission_additional_info_text?.length > 0
  );
};

export const isFlatOrUptoCommission = (brand, isDrawer = false) => {
  const isFlat = Boolean(
    brand?.min_commission === brand?.max_commission &&
      !brand?.commission_additional_info_text?.length &&
      !brand?.category_wise_commissions?.length &&
      brand?.min_commission !== "0%"
  );

  if (isDrawer) {
    return isFlat;
  } else {
    if (brand?.brand_id === MEESHO_PILOT_3_BRAND_ID) return true;
    else return isFlat;
  }
};

export const isZeroCommission = (brand) => {
  return brand.newCommission === "0%" && brand.oldCommission === "0%";
};

export const getPostTypeLabel = (post_url) => {
  if (!post_url) return "";

  const lowerUrl = post_url?.toLowerCase();

  if (lowerUrl.includes("youtube.com") || lowerUrl.includes("youtu.be")) {
    return "postyt";
  } else if (lowerUrl.includes("instagram.com")) {
    return "postig";
  } else if (lowerUrl.includes("facebook.com")) {
    return "postfb";
  } else {
    return undefined;
  }
};

export const isFeatureNew = () => {
  const today = new Date();
  const start = new Date("2025-09-08");
  const oneMonthInMs = 30 * 24 * 60 * 60 * 1000;
  return today - start < oneMonthInMs;
};

export const getPostType = (post, color = "#000") => {
  const postType = post?.post_type?.type;
  if (
    postType === POST_TYPES.INSTA_POST ||
    postType === POST_TYPES.INSTA_REEL
  ) {
    return (
      <StyledImage
        width="24px"
        height="24px"
        src={`${IMAGE_PREFIX}Property%201=IG.png`}
        alt="instagram"
      />
    );
  } else if (
    postType === POST_TYPES.YT_SHORTS ||
    postType === POST_TYPES.YT_VIDEO
  ) {
    return (
      <StyledImage
        width="24px"
        height="24px"
        src={`${IMAGE_PREFIX}Property%201=YT.png`}
        alt="youtube"
      />
    );
  } else if (
    postType === POST_TYPES.FB_POST ||
    postType === POST_TYPES.FB_VIDEO
  ) {
    return (
      <StyledImage
        width="24px"
        height="24px"
        src={`${IMAGE_PREFIX}logos_facebook.svg`}
        alt="facebook"
      />
    );
  }
  // uncomment to revert it later for all once approved by YT
  /* return (
    <img
      src={post?.post_type?.icon_url}
      style={{ width: "1.25rem", height: "1.25rem" }}
      alt=""
      className="mr-2"
    />
  ); */
};
export function isElementCompletelyVisible(className) {
  const isVisible = false;

  const element = document.querySelector("." + className);

  if (element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= window.innerHeight &&
      rect.right <= window.innerWidth
    );
  }
  return isVisible;
}

export function isElementPresentInDom(className) {
  const element = document.querySelector("." + className);
  return !!element;
}

export function isElementCompletelyVisibleAboveFooter(
  className,
  footerHeight = 84
) {
  const isVisible = false;

  const element = document.querySelector("." + className);

  if (element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= window.innerHeight - footerHeight &&
      rect.right <= window.innerWidth
    );
  }
  return isVisible;
}

export const getGaClientId = () => {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    if (
      trimmed.startsWith("_ga=GA1.1.") &&
      document.location.hostname.includes("wishlink")
    ) {
      const parts = trimmed.split(".");
      if (parts.length >= 4) {
        return `${parts[2]}.${parts[3]}`;
      }
    }
  }
  return null;
};

export const getPlatformFromStorage = () => {
  let creatorSource = {};
  try {
    creatorSource = JSON.parse(localStorage.getItem("creatorSource"));
  } catch (error) {
    // do nothing
  }
  let platform = "";
  if (creatorSource && Object.keys(creatorSource).length > 0)
    platform = creatorSource.device_platform;
  else platform = getPlatform();
  return platform;
};
